import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  TextInput,
  Button,
  FormField,
  Alert,
  Text,
} from '@vipso/shared';
import {
  useVerifyOtpMutation,
  useLoginMutation,
  useInitAppMutation,
} from '../api/tenantApi';
import {
  setAuthFromInit,
  setVerifyOtpResult,
  setPendingPhone,
} from '../store/authSlice';
import { mapInitAppPayload } from '../store/authUtils';
import type { RootState } from '../store';
import { LoginLandingLayout, loginPrimaryButtonStyle } from '../features/auth/LoginLandingLayout';
import { resolveDisplayLogoUrl } from '../utils/resolveDisplayLogoUrl';
import { resolvePostLoginOrgId } from '../utils/resolvePostLoginOrgId';

export function OtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const pendingPhone = useSelector((state: RootState) => state.auth.pendingPhone);
  const pendingOrgId = useSelector((state: RootState) => state.auth.pendingOrgId);
  const lastOrgId = useSelector((state: RootState) => state.auth.lastOrgId);
  const tenant = useSelector((state: RootState) => state.auth.tenant);
  const theme = useSelector((state: RootState) => state.auth.theme);
  const config = useSelector((state: RootState) => state.auth.config);
  const tenantOrgs = useSelector((state: RootState) => state.auth.tenantOrgs);

  const pendingOrg = pendingOrgId
    ? tenantOrgs.find((o) => String(o.id) === String(pendingOrgId))
    : undefined;
  const logoUrl = resolveDisplayLogoUrl(
    pendingOrg
      ? { logoFileId: pendingOrg.logoFileId, logoUrl: pendingOrg.logoUrl }
      : null,
    null,
    null,
    { tenantSlug: tenant?.slug },
  );
  const primaryColor = pendingOrg?.primaryColor || theme?.primaryColor || null;
  const [otp, setOtp] = useState('');
  const [verifyOtp, { isLoading: verifyLoading, error }] = useVerifyOtpMutation();
  const [initApp, { isLoading: initLoading }] = useInitAppMutation();
  const [resendOtp, { isLoading: resendLoading }] = useLoginMutation();
  const isLoading = verifyLoading || initLoading;

  // Only bounce to login when OTP was never started — not when setAuthFromInit
  // clears pendingPhone after a successful verify (that must go to dashboard).
  useEffect(() => {
    if (!pendingPhone && !accessToken) {
      navigate('/login', { replace: true });
    }
  }, [pendingPhone, accessToken, navigate]);

  if (!pendingPhone && !accessToken) {
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!otp.trim() || !pendingPhone) return;

    try {
      const result = await verifyOtp({
        phone: pendingPhone,
        otp: otp.trim(),
        tenantId: tenant?.id ?? null,
      }).unwrap();
      dispatch(setVerifyOtpResult(result.verifyOtp));

      // defaultContextLogin=true → skip picker (last/first org). false → org was chosen on login.
      const showOrgSelectOnLogin = config != null && !config.defaultContextLogin;
      const targetOrgId = resolvePostLoginOrgId(result.verifyOtp.orgs, {
        showOrgSelectOnLogin,
        pendingOrgId,
        lastOrgId,
      });

      if (targetOrgId) {
        const init = await initApp({
          userId: String(result.verifyOtp.user.id),
          orgId: targetOrgId,
        }).unwrap();
        dispatch(
          setAuthFromInit(
            mapInitAppPayload(init.initApp, { user: result.verifyOtp.user }),
          ),
        );
        navigate('/dashboard', { replace: true });
        return;
      }

      // Org was required on login but pending id is missing/not a membership —
      // fall back to manual picker of the user's orgs only.
      navigate('/org-picker');
    } catch {
      // surfaced via error state
    }
  }

  async function handleResend() {
    if (!pendingPhone) return;
    try {
      await resendOtp({ phone: pendingPhone, tenantId: tenant?.id ?? null }).unwrap();
      dispatch(setPendingPhone(pendingPhone));
    } catch {
      // surfaced via error state
    }
  }

  if (!pendingPhone) {
    // Authenticated after verify — waiting for dashboard navigation
    return null;
  }

  return (
    <LoginLandingLayout
      logoUrl={logoUrl}
      primaryColor={primaryColor}
      title={t('auth.login')}
      subtitle={t('auth.otpSentTo', { phone: pendingPhone })}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error ? (
            <Alert color="red" title={t('auth.verificationFailed')}>
              {String(
                (error as { data?: { message?: string }; message?: string })?.data?.message ??
                  (error as { message?: string })?.message ??
                  '',
              )
                .toLowerCase()
                .includes('user not found')
                ? t('auth.userNotFound')
                : t('auth.invalidOtp')}
            </Alert>
          ) : null}

          <FormField label={t('auth.verificationCode')} required>
            <TextInput
              value={otp}
              onChange={(e) => setOtp(e.currentTarget.value)}
              placeholder={t('auth.otpPlaceholder')}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
            />
          </FormField>

          <Button
            variant="outline"
            onClick={handleResend}
            loading={resendLoading}
            disabled={!pendingPhone || pendingPhone.length < 4}
            fullWidth
          >
            {t('auth.resendOtp')}
          </Button>

          <Button
            type="submit"
            loading={isLoading}
            fullWidth
            disabled={!otp || otp.length !== 4}
            style={loginPrimaryButtonStyle}
          >
            {t('auth.login')}
          </Button>

          <Text size="sm" ta="center" c="dimmed">
            <Text
              component="span"
              c={primaryColor || '#cc202e'}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/login')}
            >
              {t('auth.useDifferentNumber')}
            </Text>
          </Text>
        </Stack>
      </form>
    </LoginLandingLayout>
  );
}
